import { api } from './client';

export interface PublishTemplate {
  id: number;
  name: string;
  track: string;
  content_rating: string;
  app_content: string;
  data_safety_csv: string;
  listing_defaults: string;
  /** Its Data safety CSV is the one handoff links give outside developers. */
  for_handoff: boolean;
  created_at: number;
  updated_at: number;
}

export type PublishJobStatus = 'pending' | 'running' | 'blocked' | 'done' | 'failed';

export interface PublishJob {
  id: string;
  account_id: number;
  template_id: number;
  package_name: string;
  title: string;
  status: PublishJobStatus;
  blocked_reason: string | null;
  error: string | null;
  account_name: string;
  template_name: string;
  steps_done: number;
  steps_total: number;
  created_at: number;
  updated_at: number;
  finished_at: number | null;
}

export interface PublishStepRow {
  id: number;
  ord: number;
  step: string;
  kind: 'ui' | 'api';
  status: 'pending' | 'running' | 'done' | 'failed' | 'skipped';
  attempts: number;
  error: string | null;
  result_json: string | null;
  evidence_key: string | null;
  // Set while the ShardX runner holds the step; null for a step a human is doing.
  runner_id: string | null;
  lease_until: number | null;
  started_at: number | null;
  finished_at: number | null;
}

export interface PublishJobDetail {
  job: PublishJob & {
    track: string; default_lang: string; short_desc: string; full_desc: string;
  };
  steps: PublishStepRow[];
}

// The browser half of a Play account as the dashboard is allowed to see it:
// presence flags and a count, never a password or a seed.
export interface PublishAccount {
  id: number;
  name: string;
  enabled: number;
  google_email: string | null;
  iarc_email: string | null;
  contact_email: string | null;
  shardx_profile_id: string | null;
  session_status: string;
  last_keepalive_at: number | null;
  daily_publish_cap: number;
  has_session_evidence: boolean;
  has_password: boolean;
  has_totp: boolean;
  backup_codes_left: number;
  recovery_email: string;
  proxy_label: string;
  cookies_pending: boolean;
  platform: string;
  sa_client_email: string | null;
  onboard_requested_at: number | null;
  onboard_started_at: number | null;
  onboard_project_id: string | null;
  onboard_error: string | null;
  // A shared console: somebody else's developer account that invited the
  // Google login of this browser profile; the login (and its secrets) is the
  // profile's, the developer is pinned, the owner invites our service account.
  browser_profile_id: number | null;
  browser_profile_name: string | null;
  developer_id: string | null;
  developer_access: 'ok' | 'lost' | null;
  sa_invited_at: number | null;
}

export interface AccountSecretsView {
  google_email: string | null;
  iarc_email: string | null;
  contact_email: string | null;
  shardx_profile_id: string | null;
  session_status: string;
  last_keepalive_at: number | null;
  daily_publish_cap: number;
  has_session_evidence: boolean;
  has_password: boolean;
  has_totp: boolean;
  backup_codes_left: number;
  recovery_email: string;
  proxy_label: string;
  cookies_pending: boolean;
  platform: string;
}

// Human-readable labels for the eight pipeline steps. The four ui steps are done
// in the Play Console — by hand, or by the ShardX runner once it holds the step;
// the api steps run on the cron.
export const STEP_LABELS: Record<string, string> = {
  create_app: 'Create app',
  upload_first_aab: 'Upload first AAB (draft)',
  app_content: 'App content declarations',
  content_rating: 'Content rating (IARC)',
  store_settings: 'Store settings (category, contacts)',
  data_safety: 'Data safety',
  listing: 'Store listing',
  release: 'Release',
  send_for_review: 'Send for review',
  verify: 'Verify review state',
};

const BASE = '/admin/play-publish';

export const listTemplates = () => api.get<PublishTemplate[]>(`${BASE}/templates`);
export const createTemplate = (input: {
  name: string; track: string; data_safety_csv: string;
  content_rating?: unknown; app_content?: unknown; listing_defaults?: unknown; for_handoff?: boolean;
}) => api.post<{ id: number }>(`${BASE}/templates`, input);
export const updateTemplate = (id: number, input: Record<string, unknown>) =>
  api.patch<{ id: number }>(`${BASE}/templates/${id}`, input);
export const deleteTemplate = (id: number) => api.delete<{ id: number }>(`${BASE}/templates/${id}`);

export const listJobs = () => api.get<PublishJob[]>(`${BASE}/jobs`);
export const getJob = (id: string) => api.get<PublishJobDetail>(`${BASE}/jobs/${id}`);
export const createJob = (input: {
  account_id: number; template_id: number; package_name: string; title: string;
  app_id?: string; app_build_id?: string; build_mode?: 'existing' | 'new'; default_lang?: string;
  short_desc?: string; full_desc?: string;
}) => api.post<{ id: string }>(`${BASE}/jobs`, input);

export const completeStep = (jobId: string, step: string, result?: unknown) =>
  api.post<{ step: string }>(`${BASE}/jobs/${jobId}/steps/${step}/done`, { result });
export const retryStep = (jobId: string, step: string) =>
  api.post<{ step: string }>(`${BASE}/jobs/${jobId}/steps/${step}/retry`, {});
export const blockJob = (jobId: string, reason: string) =>
  api.post<{ reason: string }>(`${BASE}/jobs/${jobId}/block`, { reason });
export const rebuildJob = (jobId: string) =>
  api.post<{ id: string; app_build_id: string }>(`${BASE}/jobs/${jobId}/rebuild`);
/** Stop the auto-fill for manual work in the console; unblockJob resumes it. */
export const pauseJob = (jobId: string) =>
  api.post<{ id: string; released_steps: number }>(`${BASE}/jobs/${jobId}/pause`, {});
export const unblockJob = (jobId: string) =>
  api.post<{ id: string }>(`${BASE}/jobs/${jobId}/unblock`, {});

export const listPublishAccounts = () => api.get<PublishAccount[]>(`${BASE}/accounts`);
export const onboardAccount = (id: number) =>
  api.post<{ id: number; requested_at: number }>(`${BASE}/accounts/${id}/onboard`);
export const markSaInvited = (id: number, invited: boolean) =>
  api.post<{ id: number }>(`${BASE}/accounts/${id}/sa-invited`, { invited });
export const getAccountSecrets = (id: number) =>
  api.get<AccountSecretsView>(`${BASE}/accounts/${id}/secrets`);
export const putAccountSecrets = (id: number, input: {
  password?: string; totp_seed?: string; backup_codes?: string[]; recovery_email?: string;
  proxy?: string; cookies?: string; platform?: string; google_email?: string; iarc_email?: string; contact_email?: string; shardx_profile_id?: string;
  daily_publish_cap?: number;
}) => api.put<{ id: number }>(`${BASE}/accounts/${id}/secrets`, input);

// Screenshots the runner took inside a real Play Console — served through the
// worker, never a public bucket URL, because they show a signed-in account.
export const getStepEvidence = (jobId: string, step: string) =>
  api.blob(`${BASE}/jobs/${jobId}/steps/${step}/evidence`);
export const getAccountEvidence = (id: number) =>
  api.blob(`${BASE}/accounts/${id}/evidence`);
